import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { Props as IssueTableProps } from '@atlassian/jira-native-issue-table/src/ui/types.tsx';
import type {
	JiraUpdateFieldSetPreferencesInput,
	useColumnResize_updateIssueViewFieldSetPreferencesMutation as UpdateFieldSetPreferencesMutation,
	useColumnResize_updateIssueViewFieldSetPreferencesMutation$data as UpdateFieldSetPreferencesMutationData,
} from '@atlassian/jira-relay/src/__generated__/useColumnResize_updateIssueViewFieldSetPreferencesMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useColumnWidthUpdater } from '../use-column-width-updater/index.tsx';
import { onChildIssueResizeErrorFlag } from '../../common/flags/index.tsx';

interface Props {
	/** ID of the `JiraIssueSearchFieldSetConnection` record */
	fieldSetsId: string;
	onCompleted?: (success: boolean, fieldSetPreference: JiraUpdateFieldSetPreferencesInput) => void;
}

export const useColumnResize = ({ fieldSetsId, onCompleted }: Props) => {
	const cloudId = useCloudId();
	const { showFlag } = useFlagsService();
	const issueKey = useIssueKey();

	const [updateFieldSetPreferences, isInFlight] = useMutation<UpdateFieldSetPreferencesMutation>(
		graphql`
			mutation useColumnResize_updateIssueViewFieldSetPreferencesMutation(
				$cloudId: ID!
				$namespace: String
				$input: JiraFieldSetPreferencesMutationInput!
				$context: JiraIssueSearchViewFieldSetsContext
			) {
				jira {
					updateUserFieldSetPreferences(
						cloudId: $cloudId
						namespace: $namespace
						fieldSetPreferencesInput: $input
						context: $context
					) @optIn(to: "JiraUpdateUserFieldSetPreferences") {
						success
					}
				}
			}
		`,
	);

	const updateColumnWidth = useColumnWidthUpdater(fieldSetsId);

	/**
	 * onColumnResize is invoked when users resize columns on the table. For optimistic updates the
	 * optimisticUpdater gets called and we see the expected change then mutation happens,
	 * optimisticUpdater gets reverted as a result of mutation then updater gets called to persist the optimistic update view.
	 */
	const onColumnResize = useCallback<NonNullable<IssueTableProps['onColumnResize']>>(
		(columnId, width) => {
			const context = {
				issueContext: {
					issueKey,
				},
			};

			const input = {
				fieldSetId: columnId,
				width: width ?? null,
			};

			updateFieldSetPreferences({
				variables: {
					cloudId,
					namespace: 'CHILD_ISSUE_PANEL',
					input: {
						nodes: [input],
					},
					context,
				},
				optimisticUpdater(store) {
					updateColumnWidth(store, columnId, width);
				},
				updater(store) {
					updateColumnWidth(store, columnId, width);
				},
				onCompleted(data: UpdateFieldSetPreferencesMutationData) {
					const success = data.jira?.updateUserFieldSetPreferences?.success === true;

					fg('issue-jiv-20390-column-picker-disappearing-fix') && onCompleted?.(success, input);

					if (!success) {
						showFlag(onChildIssueResizeErrorFlag);
						fireErrorAnalytics({
							meta: {
								id: 'commitUpdateIssueViewFieldSetPreferencesMutationUnsuccessful',
								packageName: 'jiraIssueViewCommonViews',
								teamName: 'bento',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					showFlag(onChildIssueResizeErrorFlag);
					fireErrorAnalytics({
						meta: {
							id: 'commitUpdateIssueViewFieldSetPreferencesMutationError',
							packageName: 'jiraIssueViewCommonViews',
							teamName: 'bento',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		},
		[cloudId, issueKey, onCompleted, showFlag, updateColumnWidth, updateFieldSetPreferences],
	);

	return [onColumnResize, isInFlight] as const;
};
